import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type {
  BulkTransitionResult,
  WorkItemSummary,
  WorkItemTransition,
  WorkItemTransitionReason,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { REASON_REQUIRING_COMMENT, TRANSITION_REASONS } from "./workItemVocabulary";

/**
 * A backward move applied to several items, each with its own reason.
 *
 * <p>Failing QA is what this is for. Five bugs fail for five different reasons, and one reason
 * spread across them records something untrue about four — which the reports built on those
 * codes then repeat. So every item gets its own answer, and the shared one is a convenience for
 * the times they really did all fail for the same thing.
 */
export function BatchReasonDrawer({
  items,
  transition,
  label,
  open,
  onClose,
  onDone,
}: {
  items: WorkItemSummary[];
  transition: WorkItemTransition;
  label: string;
  open: boolean;
  onClose: () => void;
  /** True when every item moved. False leaves the selection alone so the refusal stays on screen. */
  onDone: (allMoved: boolean) => void;
}) {
  const [sharedReason, setSharedReason] = useState<WorkItemTransitionReason | "">("");
  const [sharedComment, setSharedComment] = useState("");
  const [reasons, setReasons] = useState<Record<string, WorkItemTransitionReason | "">>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<BulkTransitionResult[]>([]);

  useEffect(() => {
    if (open) {
      setSharedReason("");
      setSharedComment("");
      setReasons({});
      setComments({});
      setError(null);
      setRefused([]);
    }
  }, [open]);

  const reasonFor = (id: string) => reasons[id] || sharedReason;
  const commentFor = (id: string) => (comments[id] ?? "").trim() || sharedComment.trim();

  /* Every item needs a reason, and the codes that count for nothing on their own need words
     beside them. A batch that recorded "Other" against five bugs and said nothing would leave
     whoever reads them later no better off than no reason at all. */
  const unanswered = items.filter((item) => {
    const reason = reasonFor(item.id);
    if (!reason) {
      return true;
    }
    return REASON_REQUIRING_COMMENT.includes(reason) && commentFor(item.id) === "";
  });

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const answered = await api.post<BulkTransitionResult[]>(
        "/api/v1/work-items/bulk-transition",
        {
          transition,
          workItemIds: items.map((item) => item.id),
          reason: sharedReason || null,
          reasonComment: sharedComment.trim() || null,
          reviewerId: null,
          reasons: Object.fromEntries(
            Object.entries(reasons).filter(([, value]) => value !== ""),
          ),
          comments: Object.fromEntries(
            Object.entries(comments)
              .filter(([, value]) => value.trim() !== "")
              .map(([id, value]) => [id, value.trim()]),
          ),
        },
      );
      const stuck = answered.filter((row) => !row.moved);
      onDone(stuck.length === 0);
      if (stuck.length === 0) {
        onClose();
      } else {
        setRefused(stuck);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not move those items.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={label}
      subtitle={`${items.length} item${items.length === 1 ? "" : "s"}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={unanswered.length > 0 || saving}
          >
            {label}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          The reason is recorded against each item on its own, and it is what anybody reading it
          later has to go on.
        </InlineAlert>

        <Field
          label="The same for all of them"
          hint="Used for every item below that has nothing of its own."
        >
          <Select
            value={sharedReason}
            onChange={(event) =>
              setSharedReason(event.target.value as WorkItemTransitionReason | "")
            }
          >
            <option value="">Choose a reason…</option>
            {TRANSITION_REASONS.map(([code, text]) => (
              <option key={code} value={code}>
                {text}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="In a few words" hint="Optional here; asked for where the code needs it.">
          <Textarea
            rows={2}
            value={sharedComment}
            onChange={(event) => setSharedComment(event.target.value)}
          />
        </Field>

        <div className="space-y-3 border-t border-ink-100 pt-3">
          {items.map((item) => (
            <div key={item.id} className="space-y-2">
              <Field label={`${item.itemKey} — ${item.title}`}>
                <Select
                  value={reasons[item.id] ?? ""}
                  onChange={(event) =>
                    setReasons((current) => ({
                      ...current,
                      [item.id]: event.target.value as WorkItemTransitionReason | "",
                    }))
                  }
                >
                  <option value="">
                    {sharedReason ? "Use the shared reason" : "Choose a reason…"}
                  </option>
                  {TRANSITION_REASONS.map(([code, text]) => (
                    <option key={code} value={code}>
                      {text}
                    </option>
                  ))}
                </Select>
              </Field>
              <Textarea
                rows={2}
                aria-label={`What happened with ${item.itemKey}`}
                placeholder="Only if this one needs something said about it."
                value={comments[item.id] ?? ""}
                onChange={(event) =>
                  setComments((current) => ({ ...current, [item.id]: event.target.value }))
                }
              />
            </div>
          ))}
        </div>

        {unanswered.length > 0 && (
          <InlineAlert tone="warning">
            {unanswered.length} of these has no reason yet. Choose one above for all of them, or
            one against each.
          </InlineAlert>
        )}

        {refused.length > 0 && (
          <InlineAlert tone="warning">
            <div className="space-y-1">
              <p>{refused.length} did not move. The rest did.</p>
              {refused.map((row) => (
                <p key={row.workItemId} className="text-sm">
                  <span className="font-medium">{row.itemKey ?? "Item"}</span> —{" "}
                  {row.refusedBecause}
                </p>
              ))}
            </div>
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
